import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import ManutencaoCarrinhosService from '../../service/manutencaoCarrinhos/ManutencaoCarrinhosService';

// ─── Constantes ───────────────────────────────────────────────────────────────
const CIRCUITOS = ['DF-001', 'DF-002', 'DF-003', 'DF-004', 'DF-005', 'DF-006'];

const TIPOS = [
  { label: 'Carrinho', abbr: 'CAR' },
  { label: 'Display', abbr: 'DIS' },
  { label: 'Mesa', abbr: 'MES' },
  { label: 'Quiosque', abbr: 'QUI' },
];

const ABBR_TO_TIPO = Object.fromEntries(TIPOS.map((t) => [t.abbr, t.label]));

const ITENS = [
  { value: 'pecas-acrilico-prateleiras', label: 'Peças de acrílico das prateleiras (kit com 3)' },
  { value: 'capa-chuva', label: 'Capa de chuva' },
  { value: 'placa-pvc-preta', label: 'Placa de PVC preta (não magnética)' },
  { value: 'rodas-rolamentos-contrapesos', label: 'Rodas (kit com 2), rolamentos (kit com 4) e contrapesos (kit com 2)' },
  { value: 'ferragem', label: 'Toda a ferragem do carrinho (porcas, parafusos, etc.)' },
  { value: 'rolamento', label: 'Rolamento' },
  { value: 'eixo', label: 'Eixo' },
  { value: 'placa-magnetica', label: 'Placa magnética' },
  { value: 'cartaz-magnetico', label: 'Cartaz magnético' },
  { value: 'bolsa-transporte', label: 'Bolsa para transporte para display simples ou duplo' },
  { value: 'outros', label: 'Outros' },
];

function gerarCodigo(circuito, tipo, unidade) {
  const circ = circuito.replace('-', '');
  const abbr = TIPOS.find((t) => t.label === tipo)?.abbr || '';
  return `${circ}-${abbr}-${unidade}`;
}

function parseCodigo(codigo) {
  if (!codigo) return { circuito: '', tipo: '', unidade: '' };
  const match = codigo.match(/^(DF)(\d{3})-(CAR|DIS|MES|QUI)-(\d{3})$/);
  if (!match) return { circuito: '', tipo: '', unidade: '' };
  return {
    circuito: `${match[1]}-${match[2]}`,
    tipo: ABBR_TO_TIPO[match[3]] || '',
    unidade: match[4],
  };
}

function inferirItemSelecionado(titulo) {
  if (!titulo) return null;
  return ITENS.find((item) => titulo.includes(item.label))?.value || null;
}

// ─── Styled Components ────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 500px) {
    border-radius: 10px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px 24px;
`;

const ModalTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    font-family: 'Roboto', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #222;
    margin: 0;
  }

  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 22px;
    background: #00aaaa;
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const ModalCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover { color: #333; background: #f0f0f0; }
`;

const ModalBody = styled.div`
  padding: 8px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #333;

  span { color: #dc2626; }
`;

const FormInput = styled.input`
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  padding: 9px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #222;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus { border-color: #008080; }
  &::placeholder { color: #bbb; }
`;

const FormTextarea = styled.textarea`
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  padding: 9px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #222;
  outline: none;
  resize: vertical;
  min-height: 90px;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus { border-color: #008080; }
  &::placeholder { color: #bbb; }
`;

const FormSelect = styled.select`
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  padding: 9px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #222;
  outline: none;
  background: white;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  appearance: auto;

  &:focus { border-color: #008080; }
`;

const TipoChamadoDisplay = styled.div`
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  padding: 9px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #555;
  background: #fafafa;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TipoChamadoNota = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #888;
  margin: 0;
  font-style: italic;
`;

const BtnSalvar = styled.button`
  background: #008080;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 11px 0;
  width: 100%;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.2s;

  &:hover { background: #006666; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SectionLabel = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #008080;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 4px 0 0 0;
`;

const CamposItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;

  @media (max-width: 440px) {
    grid-template-columns: 1fr;
  }
`;

const FieldHint = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  color: ${(p) => (p.$error ? '#dc2626' : '#aaa')};
  margin-top: 2px;
`;

const CodigoPreviewBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #e0f2f1;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: #008080;
`;

const CodigoPreviewValor = styled.span`
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 13px;
  font-weight: 700;
  color: #005f5f;
  letter-spacing: 0.5px;
`;

const TitleFieldWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;
  background: white;

  &:focus-within {
    border-color: #008080;
  }
`;

const CodigoChip = styled.span`
  background: #e0f2f1;
  color: #005f5f;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  white-space: nowrap;
  flex-shrink: 0;
  border-right: 1.5px solid #b2dfdb;
  display: flex;
  align-items: center;
`;

const TitleComplementInput = styled.input`
  border: none;
  outline: none;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #222;
  padding: 9px 14px;
  flex: 1;
  background: transparent;
  min-width: 0;

  &::placeholder { color: #bbb; }
`;

const FormInputDisabled = styled.input`
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  padding: 9px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  color: #bbb;
  background: #fafafa;
  width: 100%;
  box-sizing: border-box;
  cursor: not-allowed;
`;

// ─── Dados (mantido apenas como fallback vazio) ──────────────────────────────────────────────

// ─── Componente ───────────────────────────────────────────────────────────────
const ModalChamado = ({ chamado, onClose, onSalvar }) => {
  const modoEdicao = !!chamado;
  const parsed = parseCodigo(chamado?.codigo);

  const [circuito, setCircuito] = useState(parsed.circuito || '');
  const [tipo, setTipo] = useState(parsed.tipo || '');
  const [unidadeInput, setUnidadeInput] = useState(parsed.unidade || '');
  const [unidade, setUnidade] = useState(parsed.unidade || '');
  const [itemSelecionado, setItemSelecionado] = useState(() => inferirItemSelecionado(chamado?.titulo || ''));
  const [descricao, setDescricao] = useState(chamado?.descricao || '');

  // Responsáveis carregados da API
  const [responsaveis, setResponsaveis] = useState([]);
  const [principal, setPrincipal] = useState(null);      // { _id, usuario: { _id, nome } }
  const [responsavelId, setResponsavelId] = useState(
    modoEdicao ? (chamado?.atribuidoAId || '') : ''
  );

  useEffect(() => {
    ManutencaoCarrinhosService.listarResponsaveis()
      .then((lista) => {
        setResponsaveis(lista);
        const p = lista.find((r) => r.principal);
        setPrincipal(p || null);
        if (!modoEdicao && p) {
          setResponsavelId(p.usuario._id);
        }
      })
      .catch(() => {}); // falha silenciosa — não bloqueia o modal
  }, [modoEdicao]);

  const codigoGerado = useMemo(() => {
    if (!circuito || !tipo || !unidade) return '';
    return gerarCodigo(circuito, tipo, unidade);
  }, [circuito, tipo, unidade]);

  const unidadeValida =
    unidadeInput === '' ||
    (/^\d+$/.test(unidadeInput) &&
      parseInt(unidadeInput, 10) >= 1 &&
      parseInt(unidadeInput, 10) <= 70);

  const handleUnidadeChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 3);
    setUnidadeInput(v);
    setUnidade('');
  };

  const handleUnidadeBlur = () => {
    const n = parseInt(unidadeInput, 10);
    if (!isNaN(n) && n >= 1 && n <= 70) {
      const padded = String(n).padStart(3, '0');
      setUnidade(padded);
      setUnidadeInput(padded);
    } else {
      setUnidade('');
    }
  };

  const itemAtivo = ITENS.find((item) => item.value === itemSelecionado) || null;
  const tituloCompleto = codigoGerado && itemAtivo ? `${codigoGerado} ${itemAtivo.label}` : '';
  const isValid = !!(codigoGerado && itemAtivo && descricao.trim());

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSalvar = () => {
    if (!isValid) return;
    onSalvar({
      titulo: tituloCompleto,
      atribuidoAId: responsavelId || null,
      descricao: descricao.trim(),
      codigo: codigoGerado,
    });
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalBox>
        <ModalHeader>
          <ModalTitulo><h2>{modoEdicao ? 'Editar Chamado' : 'Novo Chamado'}</h2></ModalTitulo>
          <ModalCloseBtn onClick={onClose} aria-label="Fechar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </ModalCloseBtn>
        </ModalHeader>
        <ModalBody>

          {/* ── Identificação do item ── */}
          <SectionLabel>Identificação do item</SectionLabel>
          <CamposItemGrid>
            <FormField>
              <FormLabel>Circuito <span>*</span></FormLabel>
              <FormSelect value={circuito} onChange={(e) => setCircuito(e.target.value)}>
                <option value="">Selecionar</option>
                {CIRCUITOS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel>Tipo <span>*</span></FormLabel>
              <FormSelect value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="">Selecionar</option>
                {TIPOS.map((t) => (
                  <option key={t.abbr} value={t.label}>{t.label}</option>
                ))}
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel>Unidade <span>*</span></FormLabel>
              <FormInput
                value={unidadeInput}
                onChange={handleUnidadeChange}
                onBlur={handleUnidadeBlur}
                placeholder="001–070"
                inputMode="numeric"
                style={!unidadeValida && unidadeInput !== '' ? { borderColor: '#dc2626' } : {}}
              />
              {!unidadeValida && unidadeInput !== '' && (
                <FieldHint $error>Digite um número de 1 a 70</FieldHint>
              )}
            </FormField>
          </CamposItemGrid>

          <FormField>
            <FormLabel>Item</FormLabel>
            <FormSelect value={itemSelecionado || ''} onChange={(e) => setItemSelecionado(e.target.value)}>
              <option value="">Selecionar</option>
              {ITENS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </FormSelect>
          </FormField>

          {/* ── Título ── */}
          <FormField>
            <FormLabel>Título <span>*</span></FormLabel>
            {tituloCompleto ? (
              <TitleFieldWrapper>
                <CodigoChip>{codigoGerado}</CodigoChip>
                <TitleComplementInput value={itemAtivo ? itemAtivo.label : ''} readOnly />
              </TitleFieldWrapper>
            ) : (
              <FormInputDisabled
                readOnly
                placeholder="Preencha circuito, tipo e unidade e selecione o item"
              />
            )}
          </FormField>

          {/* ── Tipo de chamado ── */}
          <FormField>
            <FormLabel>Tipo de chamado</FormLabel>
            <TipoChamadoDisplay>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h6M19 5v6"/>
              </svg>
              Manutenção de carrinhos
            </TipoChamadoDisplay>
            <TipoChamadoNota>Nesta versão, apenas manutenção de carrinhos está disponível.</TipoChamadoNota>
          </FormField>

          {/* ── Responsável ── */}
          <FormField>
            <FormLabel>Atribuir responsável</FormLabel>
            {modoEdicao ? (
              <FormSelect
                value={responsavelId}
                onChange={(e) => setResponsavelId(e.target.value)}
              >
                <option value="">Selecionar responsável</option>
                {responsaveis.map((r) => (
                  <option key={r._id} value={r.usuario._id}>{r.usuario.nome}</option>
                ))}
              </FormSelect>
            ) : (
              <>
                <TipoChamadoDisplay>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {principal ? principal.usuario.nome : 'Carregando...'}
                </TipoChamadoDisplay>
                <TipoChamadoNota>Chamados de manutenção são encaminhados para o encarregado principal. Após receber, ele poderá atribuir a outro membro.</TipoChamadoNota>
              </>
            )}
          </FormField>

          {/* ── Descrição ── */}
          <FormField>
            <FormLabel>Descrição <span>*</span></FormLabel>
            <FormTextarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o problema detalhadamente..."
            />
          </FormField>

          <BtnSalvar onClick={handleSalvar} disabled={!isValid}>
            Salvar
          </BtnSalvar>
        </ModalBody>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ModalChamado;
